import { useState, useRef, useEffect, useCallback } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useInView } from 'react-intersection-observer';
import { TextField, Box, Button } from '@material-ui/core';
import { ChevronLeft, ChevronRight, Send } from '@material-ui/icons';
import { Store, iNotification } from 'react-notifications-component';
import { useConnect } from '../../../context/connect';
import { useStyles } from './style';
import { filterCannedResponsesExtra } from '../../../helpers/filterCannedResponses';

const notification: iNotification = {
  title: "Attention",
  message: "Your message must contain no more than 1024 characters!",
  type: "danger",
  insert: "top",
  container: "top-right",
  animationIn: ["animate__animated animate__fadeIn"],
  animationOut: ["animate__animated animate__fadeOut"],
  dismiss: {
    duration: 5000,
  }
}

export const CustomTextField = () => {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [error, setError] = useState(false)
  const [mode, setMode] = useState<'shortcuts' | 'responses'>('shortcuts');
  const { shortcuts, cannedResponsesPopup, agentChatSession } = useConnect();
  const [visibleContent, setVisibleContent] = useState<string[]>([]);
  const [visibleShortcuts, setVisibleShortcuts] = useState<string[]>([]);
  const [selector, setSelector] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [text, setText] = useState<string>('');
  const list = useRef<HTMLDivElement>(null);
  const [ref, inViewEnd] = useInView();
  const maxContent = 1024;

  useEffect(() => {
    if (inViewEnd) {
      setVisibleContent((prev) => [...prev, ...visibleShortcuts.slice(20)]);
    }
  }, [inViewEnd, visibleShortcuts]);

  const sendMessage = () => {
    if (agentChatSession && text) {
      if(text.length > maxContent) {
        setError(true)
        Store.addNotification({
          ...notification,
        })
      } else {
        agentChatSession.sendMessage({
          contentType: 'text/plain',
          message: text,
        });
        setText('');
      }
    }
  };

  const clickHandler = useCallback(
    (value?: string) => {
      if (!value) {
        if (mode === 'responses') {
          setVisibleContent(
            shortcuts
              .filter((i: string) => `#${i}`.includes(`#${searchText}`))
              .slice(0, 20)
          );
          return setMode('shortcuts');
        }
        setVisibleContent([]);
        setSearchText('');
        return setOpen(false);
      }
      if (mode === 'shortcuts') {
        setVisibleContent(
          filterCannedResponsesExtra(cannedResponsesPopup, value)
        );
        return setMode('responses');
      }
      setText((prev) => prev.replace(`#${searchText}`, value));
      setOpen(false);
      setSearchText('');
      setMode('shortcuts');
    },
    [cannedResponsesPopup, mode, searchText, shortcuts]
  );

  const keyboardEventHandler = useCallback(
    (key: string) => {
      if (!list.current) return;

      const popup = list.current;
      if (popup) {
        if (key === 'ArrowRight' || key === 'Enter') {
          const value = popup.querySelector('.focus .value')?.innerHTML;

          if (!value) return;
          return clickHandler(value);
        }

        if (key === 'ArrowLeft') {
          return clickHandler();
        }

        const currentElem = popup.querySelector('.focus');
        const nextElem = currentElem?.nextElementSibling;
        const prevElem = currentElem?.previousElementSibling;
        const popupHeight = popup.closest('#popup')?.clientHeight || 240;
        const scrollContainer = popup.closest('.scrollbar-container');
        const scrollTop = scrollContainer?.scrollTop || 0;
        const offsetTop = (nextElem as HTMLElement)?.offsetTop || 0;
        const prevOffsetTop = (prevElem as HTMLElement)?.offsetTop || 0;
        const elemHeight = (nextElem as HTMLElement)?.clientHeight || 0;

        if (key === 'ArrowDown') {
          if (currentElem && nextElem) {
            const missingScroll =
              offsetTop + elemHeight - (popupHeight + scrollTop);
            if (missingScroll > 0 && scrollContainer) {
              scrollContainer.scrollTop =
                scrollContainer.scrollTop + missingScroll;
            }

            currentElem.classList.remove('focus');
            nextElem.classList.add('focus');
          }
        }

        if (key === 'ArrowUp') {
          if (
            currentElem &&
            prevElem &&
            prevElem.attributes[1].name === 'data-index'
          ) {
            const missingScroll = prevOffsetTop - scrollTop;
            if (missingScroll < 0 && scrollContainer) {
              scrollContainer.scrollTop =
                scrollContainer.scrollTop + missingScroll;
            }
            currentElem.classList.remove('focus');
            prevElem.classList.add('focus');
          }
        }
      }
    },
    [clickHandler]
  );

  const addInitialFocuse = (parent: HTMLDivElement) => {
    const children = parent.children;
    const existingFocused = parent.querySelector('.focus');
    if (existingFocused) {
      existingFocused.classList.remove('focus');
    }
    if (children[0]) {
      children[0].classList.add('focus');
    }
  };
  const changeFocusOnMouseMove = (el: MouseEvent) => {
    const currentFocusedElement = list.current?.querySelector('.focus');
    const element = el.target as HTMLElement;

    if (
      element.attributes[1] &&
      element.attributes[1].name === 'data-index' &&
      element.attributes[1].value !== currentFocusedElement?.attributes[1].value
    ) {
      if (currentFocusedElement) {
        currentFocusedElement.classList.remove('focus');
      }
      element.classList.add('focus');
    }
  };

  const mouseEventHandler = useCallback(() => {
    const parent = list.current;

    if (!parent) return;

    addInitialFocuse(parent);

    parent.addEventListener('mouseover', changeFocusOnMouseMove);
  }, []);

  useEffect(() => {
    const closePopupHandle = (ev: MouseEvent) => {
      const element = ev.target as HTMLElement;
      const popup = element.closest('#popup');
      const textarea = element.closest('#textarea');
      const btnBox = element.closest('#btn-box');

      if (!popup && !textarea && !btnBox) {
        setOpen(false);
        setSearchText('');
      }

      if (popup) {
        if (element.classList.contains('back') || element.closest('.back')) {
          return clickHandler();
        }
        const value = element.classList.contains('focus')
          ? element.querySelector('.value')?.innerHTML
          : element.classList.contains('value')
            ? element.innerHTML
            : element.closest('.focus')?.querySelector('.value')?.innerHTML;

        if (!value) return;

        clickHandler(value);
      }
    };
    window.document.addEventListener('click', closePopupHandle);

    return () => {
      window.document.removeEventListener('click', closePopupHandle);
    };
  }, [clickHandler]);

  useEffect(() => {
    setSearchText('');
    setVisibleContent([]);
    setMode('shortcuts');
    setText('');
    setOpen(false);
    //@ts-ignore
  }, [agentChatSession?.controller?.contactId]);

  useEffect(() => {
    mouseEventHandler();
  }, [open, searchText, mouseEventHandler, mode]);

  const isPopupVisible = Boolean(open && visibleContent.length);

  return (
    <>
      {isPopupVisible && <Box className={classes.back_drop}></Box>}
      <Box className={`${classes.wrapper} wrapper ${error && 'error'}`}>
        {isPopupVisible && (
          <Box className={classes.popup} id="popup">
            <PerfectScrollbar style={{ maxHeight: '240px' }}>
              <div ref={list} style={{ position: 'relative' }}>
                {visibleContent.map((i: any, idx: number) => (
                  <Box
                    className={classes.shortcut}
                    key={i}
                    display={'flex'}
                    justifyContent={'space-between'}
                    data-index={idx}
                  >
                    {mode === 'responses' && (
                      <ChevronLeft
                        className="back"
                        style={{
                          fontSize: '14px',
                          lineHeight: '18px',
                          margin: '3px 15px 0 0',
                          opacity: '0.7',
                        }}
                      />
                    )}
                    <Box
                      className="value"
                      flex={1}
                      maxWidth={'calc(100% - 20px)'}
                    >
                      {i}
                    </Box>
                    {mode === 'shortcuts' && (
                      <ChevronRight
                        style={{
                          fontSize: '14px',
                          lineHeight: '18px',
                          margin: '3px 0 0 15px',
                          opacity: '0.7',
                        }}
                      />
                    )}
                  </Box>
                ))}
                {mode === 'shortcuts' &&
                  visibleContent.length < visibleShortcuts.length && (
                    <div className={classes.in_view} ref={ref}></div>
                  )}
              </div>
            </PerfectScrollbar>
          </Box>
        )}
        <TextField
          id="textarea"
          fullWidth
          variant="outlined"
          className={classes.textfield}
          placeholder="Type message and press enter to send"
          multiline
          maxRows={3}
          value={text}
          onKeyDown={(e) => {
            const arrowKeys = [
              'ArrowRight',
              'ArrowLeft',
              'ArrowDown',
              'ArrowUp',
              'Enter',
            ];
            if (arrowKeys.includes(e.nativeEvent.key) && list.current) {
              e.preventDefault();
              keyboardEventHandler(e.nativeEvent.key);
            }

            if (e.nativeEvent.key === '#') {
              setVisibleContent(shortcuts.slice(0, 20));
              setVisibleShortcuts(shortcuts);
              setMode('shortcuts');
              setOpen(true);
            }

            if (
              e.nativeEvent.key === 'Enter' &&
              !e.shiftKey &&
              agentChatSession &&
              !isPopupVisible &&
              text
            ) {
              e.preventDefault();
              sendMessage();
            }
          }}
          onBlur={(e) => {
            setSelector(e.target.selectionStart || 0);
          }}
          onChange={(e) => {
            setText(e.target.value);
            if (e.target.value.includes('#') && open) {
              const searchValue = e.target.value
                .slice(0, e.target.selectionStart || undefined)
                .split('#')[1];
              setSearchText(searchValue);
              setVisibleContent(
                shortcuts
                  .filter((i: string) => `#${i}`.includes(`#${searchValue}`))
                  .slice(0, 20)
              );
              setVisibleShortcuts(
                shortcuts.filter((i: string) =>
                  `#${i}`.includes(`#${searchValue}`)
                )
              );
            }
            if(e.target.value.length < maxContent) {
              setError(false)
            }
            if (open && !e.target.value.includes('#')) {
              setOpen(false);
              setVisibleContent([]);
              setSearchText('');
            }
          }}
        />
        <Box
          id={'btn-box'}
          display={'flex'}
          alignItems={'center'}
          className={classes.send_btn_wrapper}
        >
          <Box
            className={classes.hash}
            onClick={(e) => {
              //@ts-ignore
              e.currentTarget
                .closest('.wrapper')
                ?.querySelector('#textarea')
                //@ts-ignore
                .focus();
              setText((prev) => {
                if (selector) {
                  return `${prev.slice(0, selector)}#${prev.slice(selector)}`;
                }
                return `${prev}#`;
              });
              setVisibleContent(shortcuts.slice(0, 20));
              setVisibleShortcuts(shortcuts);
              setMode('shortcuts');
              setOpen(true);
            }}
          >
            #
          </Box>
          <Button
            variant="contained"
            color={'primary'}
            size="small"
            onClick={() => {
              sendMessage();
            }}
            style={{
              background: 'rgb(37, 124, 175)',
              borderRadius: '2px',
              boxShadow: 'none',
            }}
          >
            <Box display="flex" alignItems="center">
              <Box
                mr={0.5}
                mt={0.3}
                style={{
                  textTransform: 'none',
                }}
              >
                SEND
              </Box>
              <Send fontSize="small" style={{ fontSize: '16px' }} />
            </Box>
          </Button>
        </Box>
      </Box>
    </>
  );
};
